import { HelcimCardAudit, HelcimClient, User, UserClient } from '@kalos/kalos-rpc';
import {
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@kalos/ui';
import { useQuery } from '@tanstack/react-query';
import {
  type Column,
  type ColumnDef,
  getCoreRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { formatUtcRelativeDateTime } from '../utils/date';
import { cardOnFileActorsQueryKey, helcimCardAuditQueryKey } from './cardOnFileQueryKey';

/** Keep in sync with ROWS_PER_PAGE in dashboard constants. */
const ROWS_PER_PAGE = 25;

const ALL = 'all';
const CUSTOMER = 'customer';

// helcim_card_audit_type ids (kalos-core migration 399).
const AUDIT_TYPE_LABELS: Record<number, string> = {
  1: 'Searched Helcim profiles',
  2: 'Linked Helcim profile',
  3: 'Relinked Helcim profile',
  4: 'Card saved',
  5: 'Card authorized',
  6: 'Card removed',
  7: 'Card link sent',
  8: 'Card link re-sent',
  9: 'Card link canceled',
};

// card_authorization_basis_type ids (kalos-core migration 397).
const BASIS_LABELS: Record<number, string> = {
  1: 'customer approval via link',
  2: 'confirmation email',
  3: 'recorded line (COF)',
};

const actionLabel = (entry: HelcimCardAudit): string => {
  const base = AUDIT_TYPE_LABELS[entry.auditTypeId] ?? `Card event #${entry.auditTypeId}`;
  const basis = entry.basisTypeId > 0 ? BASIS_LABELS[entry.basisTypeId] : '';
  return basis ? `${base} — ${basis}` : base;
};

const HEADER_CLASSES = 'font-semibold uppercase tracking-wider text-gray-400';

const LOADING_ROWS = Array.from({ length: 5 }, () => HelcimCardAudit.create());

const EMPTY_ACTOR_NAMES: Record<number, string> = {};

/**
 * The card-on-file consent trail for a customer (helcim_card_audit): searches,
 * links, sends, saves, authorizations with their basis, removals. Rendered as
 * the modal's Card Activity view on every surface — mounts fresh per visit.
 * Mirrors the house log pattern (ActivityCard): shared DataTable, server-side
 * pagination (pageNumber, id DESC) with the person filter applied server-side;
 * search filters the loaded page client-side and hides pagination while typing.
 */
export const CardOnFileAuditLog: FC<{ userId: number; grpcBaseUrl: string }> = ({
  userId,
  grpcBaseUrl,
}) => {
  const helcimClient = useMemo(() => new HelcimClient(grpcBaseUrl), [grpcBaseUrl]);
  const userClient = useMemo(() => new UserClient(grpcBaseUrl), [grpcBaseUrl]);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<string>(ALL);

  const auditQuery = useQuery({
    queryKey: [...helcimCardAuditQueryKey(userId), page, person],
    queryFn: async () => {
      const filter = HelcimCardAudit.create({
        userId,
        pageNumber: page,
        orderBy: 'id',
        orderDir: 'DESC',
      });
      if (person === CUSTOMER) {
        filter.actingUserId = 0;
        filter.fieldMask = ['ActingUserId'];
      } else if (person !== ALL) {
        filter.actingUserId = Number(person);
      }
      const res = await helcimClient.batchGetHelcimCardAudit(filter);
      if (!res) throw new Error('Failed to load card audit entries');
      return res;
    },
    enabled: !!userId && userId > 0,
    // The log mounts fresh each visit; zero staleness makes every visit a new fetch.
    staleTime: 0,
  });

  const allEntries = useMemo(() => auditQuery.data?.results ?? [], [auditQuery.data]);
  // Count survives page flips so the pager doesn't collapse while a page loads.
  const [totalCount, setTotalCount] = useState(0);
  useEffect(() => {
    if (auditQuery.data) setTotalCount(auditQuery.data.totalCount);
  }, [auditQuery.data]);

  // The person dropdown sources from the newest unfiltered page (ActivityCard's
  // peopleQuery shape) so its options don't collapse to the person being filtered.
  const peopleQuery = useQuery({
    queryKey: [...helcimCardAuditQueryKey(userId), 'people'],
    queryFn: async () => {
      const res = await helcimClient.batchGetHelcimCardAudit(
        HelcimCardAudit.create({ userId, pageNumber: 0, orderBy: 'id', orderDir: 'DESC' }),
      );
      if (!res) throw new Error('Failed to load card audit entries');
      return res;
    },
    enabled: !!userId && userId > 0,
    staleTime: 60_000,
  });
  const peopleEntries = useMemo(() => peopleQuery.data?.results ?? [], [peopleQuery.data]);

  // Resolve the distinct staff actors across the loaded page and the people
  // source by id.
  const actorIds = useMemo(
    () => [
      ...new Set(
        [...allEntries, ...peopleEntries].map((e) => e.actingUserId).filter((id) => id > 0),
      ),
    ],
    [allEntries, peopleEntries],
  );
  const actorNamesQuery = useQuery({
    queryKey: cardOnFileActorsQueryKey(actorIds),
    enabled: actorIds.length > 0,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const users = await Promise.all(actorIds.map((id) => userClient.Get(User.create({ id }))));
      const names: Record<number, string> = {};
      users.forEach((u, i) => {
        if (u) names[actorIds[i]] = `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
      });
      return names;
    },
  });
  // react-table requires referentially stable data/columns. Everything feeding
  // them derives from this one cached object — a fresh identity per render here
  // (the previous per-actor useQueries) kept re-rendering the table without end
  // once search state changed, freezing the tab.
  const actorNameById = actorNamesQuery.data ?? EMPTY_ACTOR_NAMES;

  const personName = useCallback(
    (entry: HelcimCardAudit): string => {
      if (entry.actingUserId === 0) return 'Customer';
      return actorNameById[entry.actingUserId] || `Staff #${entry.actingUserId}`;
    },
    [actorNameById],
  );

  // People who appear in the loaded trail, for the filter dropdown.
  const { people, hasCustomer } = useMemo(() => {
    const list = actorIds
      .map((id) => ({ id, name: actorNameById[id] || `Staff #${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return {
      people: list,
      hasCustomer: [...allEntries, ...peopleEntries].some((e) => e.actingUserId === 0),
    };
  }, [actorIds, actorNameById, allEntries, peopleEntries]);

  // The person filter runs server-side (actingUserId); search filters the
  // loaded page client-side, like ActivityCard.
  const pageRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allEntries;
    return allEntries.filter(
      (e) =>
        actionLabel(e).toLowerCase().includes(term) ||
        e.note.toLowerCase().includes(term) ||
        personName(e).toLowerCase().includes(term),
    );
  }, [allEntries, search, personName]);

  const pageCount = Math.ceil(totalCount / ROWS_PER_PAGE);

  const columns = useMemo<ColumnDef<HelcimCardAudit>[]>(
    () => [
      {
        id: 'action',
        enableSorting: false,
        header: ({ column }: { column: Column<HelcimCardAudit> }) => (
          <DataTableColumnHeader
            column={column}
            title="Action"
            hideVisibilityToggle
            className={HEADER_CLASSES}
          />
        ),
        cell: ({ row }: { row: Row<HelcimCardAudit> }) => (
          <div className="min-w-0">
            <p className="text-sm text-gray-900">{actionLabel(row.original)}</p>
            {!!row.original.note && (
              <p className="truncate text-xs text-gray-500">{row.original.note}</p>
            )}
          </div>
        ),
      },
      {
        id: 'person',
        enableSorting: false,
        header: ({ column }: { column: Column<HelcimCardAudit> }) => (
          <DataTableColumnHeader
            column={column}
            title="Person"
            hideVisibilityToggle
            className={HEADER_CLASSES}
          />
        ),
        cell: ({ row }: { row: Row<HelcimCardAudit> }) => (
          <span className="text-sm text-gray-700">{personName(row.original)}</span>
        ),
      },
      {
        accessorKey: 'createdAt',
        enableSorting: false,
        header: ({ column }: { column: Column<HelcimCardAudit> }) => (
          <DataTableColumnHeader
            column={column}
            title="Date Occurred"
            hideVisibilityToggle
            className={HEADER_CLASSES}
          />
        ),
        cell: ({ row }: { row: Row<HelcimCardAudit> }) => (
          <span className="text-sm text-gray-600 lg:whitespace-nowrap">
            {formatUtcRelativeDateTime(row.original.createdAt)}
          </span>
        ),
      },
    ],
    [personName],
  );

  const loadingColumns = useMemo<ColumnDef<HelcimCardAudit>[]>(
    () => columns.map((c) => ({ ...c, cell: () => <Skeleton className="h-4 w-full" /> })),
    [columns],
  );

  const table = useReactTable<HelcimCardAudit>({
    data: auditQuery.isFetching ? LOADING_ROWS : pageRows,
    columns: auditQuery.isFetching ? loadingColumns : columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (auditQuery.isError) {
    return <p className="text-sm text-red-600">Could not load card activity. Try again later.</p>;
  }

  // Only claim an empty trail when nothing is filtered — a person-filtered empty
  // page must keep the controls visible so the filter can be cleared.
  if (!auditQuery.isFetching && totalCount === 0 && person === ALL) {
    return <p className="text-sm text-gray-500">No card-on-file activity for this customer yet.</p>;
  }

  return (
    <div>
      <div className="flex flex-col gap-2 pb-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            ignoreScrolling
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search card activity…"
            className="pl-9"
          />
        </div>
        <Select
          value={person}
          onValueChange={(v) => {
            setPerson(v);
            setPage(0);
          }}
        >
          <SelectTrigger className="sm:w-48">
            <SelectValue placeholder="All people" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All people</SelectItem>
            {hasCustomer && <SelectItem value={CUSTOMER}>Customer</SelectItem>}
            {people.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="max-h-[24rem] max-w-full overflow-y-auto rounded-lg border border-gray-200">
        <DataTable table={table} useMobileCard headerClassName="bg-white shadow-none" />
      </div>

      {pageCount > 1 && !search.trim() && (
        <div className="flex justify-center pt-3">
          <DataTablePagination currentPage={page} setPage={setPage} pageCount={pageCount} />
        </div>
      )}
    </div>
  );
};
